import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import ShareButton from '@/components/ShareButton';
import { generateSingleAnalysisWord } from '@/utils/wordGenerator';
import { generatePDFFromHTML } from '@/utils/pdfGenerator';
import { 
  FileText, Download, Share2, Eye, Loader2, RefreshCw, 
  Image, Video, Type, Link as LinkIcon, Sparkles, Filter,
  ArrowLeft, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const MyContentAnalyses = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    console.log('🚀 MyContentAnalyses mounted');
    console.log('👤 User:', user);
    console.log('🌐 Current URL:', window.location.href);
    console.log('🔗 Origin:', window.location.origin);
    
    if (user) {
      loadAnalyses();
    } else {
      console.warn('⚠️ No user found, redirecting to login');
      setLoading(false);
      setError('Please log in to view your content analyses');
    }
  }, [user]);

  const loadAnalyses = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get authentication token
      const token = localStorage.getItem('authToken');
      console.log('🔑 Auth token exists:', !!token);
      console.log('🔑 Token length:', token?.length || 0);
      
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      // Construct API URL - try current domain first
      const apiUrl = `${window.location.origin}/api/analyses`;
      console.log('📡 API URL:', apiUrl);

      // Make request with detailed logging
      console.log('🌐 Making request to:', apiUrl);
      console.log('📋 Request params:', { limit: 100 });
      console.log('🔐 Authorization header will be added');

      const response = await axios.get(apiUrl, {
        params: { limit: 100 },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        withCredentials: true,
        timeout: 30000 // 30 second timeout
      });

      console.log('✅ Response received:', response.status);
      console.log('📊 Data length:', response.data?.length || 0);

      if (!response.data) {
        throw new Error('No data received from server');
      }

      // Sort analyses by timestamp (newest first)
      const sortedAnalyses = [...response.data].sort((a, b) => {
        const dateA = new Date(a.timestamp);
        const dateB = new Date(b.timestamp);
        return dateB - dateA;
      });

      console.log('✅ Analyses loaded successfully:', sortedAnalyses.length);
      setAnalyses(sortedAnalyses);
      setLoading(false);
      
      if (sortedAnalyses.length === 0) {
        toast.info('No analyses found. Start analyzing content!');
      }
      
    } catch (err) {
      console.error('❌ Error loading analyses:', err);
      console.error('❌ Error message:', err.message);
      console.error('❌ Error response:', err.response?.data);
      console.error('❌ Error status:', err.response?.status);
      
      let errorMessage = 'Failed to load analyses. ';
      
      if (err.response?.status === 401) {
        errorMessage += 'Your session has expired. Please log in again.';
        localStorage.removeItem('authToken');
        setTimeout(() => navigate('/login'), 3000);
      } else if (err.response?.status === 503) {
        errorMessage += 'Service temporarily unavailable. Please try again in a moment.';
      } else if (err.code === 'ECONNABORTED') {
        errorMessage += 'Request timed out. Please check your connection.';
      } else if (!navigator.onLine) {
        errorMessage += 'No internet connection detected.';
      } else {
        errorMessage += err.message || 'Unknown error occurred.';
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleRetry = () => {
    console.log('🔄 Retrying... Attempt:', retryCount + 1);
    setRetryCount(prev => prev + 1);
    loadAnalyses();
  };

  const handleDownloadPDF = async (analysis) => {
    try {
      toast.loading('Generating PDF report...');
      
      // Create HTML content for PDF
      const htmlContent = `
        <div style="font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto;">
          <h1 style="color: #1e293b; border-bottom: 3px solid #6366f1; padding-bottom: 10px;">
            Content Analysis Report
          </h1>
          
          <div style="margin: 30px 0;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px;">
              <div>
                <strong style="color: #64748b;">Content Type:</strong>
                <div style="color: #1e293b; font-size: 18px; margin-top: 5px;">${analysis.content_type}</div>
              </div>
              <div>
                <strong style="color: #64748b;">Dominant Emotion:</strong>
                <div style="color: #1e293b; font-size: 18px; margin-top: 5px;">${analysis.dominant_emotion}</div>
              </div>
            </div>
            
            <div style="margin-bottom: 20px;">
              <strong style="color: #64748b;">Analysis Date:</strong>
              <div style="color: #1e293b; margin-top: 5px;">${new Date(analysis.timestamp).toLocaleString()}</div>
            </div>
          </div>

          ${analysis.emotions ? `
            <div style="margin: 30px 0;">
              <h2 style="color: #1e293b; border-bottom: 2px solid #e2e8f0; padding-bottom: 8px;">Emotion Breakdown</h2>
              <div style="margin-top: 20px;">
                ${Object.entries(analysis.emotions).map(([emotion, score]) => `
                  <div style="margin-bottom: 15px;">
                    <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
                      <span style="color: #475569; text-transform: capitalize;">${emotion}</span>
                      <span style="color: #1e293b; font-weight: bold;">${(score * 100).toFixed(1)}%</span>
                    </div>
                    <div style="background: #e2e8f0; height: 8px; border-radius: 4px; overflow: hidden;">
                      <div style="background: linear-gradient(to right, #6366f1, #8b5cf6); height: 100%; width: ${score * 100}%; border-radius: 4px;"></div>
                    </div>
                  </div>
                `).join('')}
              </div>
            </div>
          ` : ''}

          <div style="margin-top: 40px; padding: 20px; background: #f1f5f9; border-radius: 8px;">
            <p style="color: #64748b; margin: 0; font-size: 12px;">
              Generated by EmoQ.ai - Emotional Intelligence for Your Content
            </p>
          </div>
        </div>
      `;

      const container = document.createElement('div');
      container.innerHTML = htmlContent;
      document.body.appendChild(container);
      
      await generatePDFFromHTML(container, `analysis-${analysis.id}.pdf`);
      
      document.body.removeChild(container);
      toast.dismiss();
      toast.success('PDF downloaded successfully!');
      
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.dismiss();
      toast.error('Failed to generate PDF. Please try again.');
    }
  };

  const handleDownloadWord = async (analysis) => {
    try {
      toast.loading('Generating Word document...');
      await generateSingleAnalysisWord(analysis);
      toast.dismiss();
      toast.success('Word document downloaded!');
    } catch (error) {
      console.error('Word generation error:', error);
      toast.dismiss();
      toast.error('Failed to generate Word document.');
    }
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'image': return <Image className="w-4 h-4" />;
      case 'video': return <Video className="w-4 h-4" />;
      case 'text': return <Type className="w-4 h-4" />;
      case 'link': return <LinkIcon className="w-4 h-4" />;
      default: return <Sparkles className="w-4 h-4" />;
    }
  };

  const filteredAnalyses = filterType === 'all' 
    ? analyses 
    : analyses.filter(a => a.content_type === filterType);

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-6">
        <Card className="w-full max-w-md bg-slate-800/50 border-slate-700">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-16 h-16 text-indigo-400 animate-spin mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              Loading Your Content Analyses
            </h3>
            <p className="text-gray-400">
              Fetching your analysis history...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-6">
        <Card className="w-full max-w-md bg-slate-800/50 border-red-500/50">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-3">
              Unable to Load Analyses
            </h3>
            <p className="text-gray-300 mb-6 text-sm leading-relaxed">
              {error}
            </p>
            <div className="flex gap-3 justify-center">
              <Button 
                onClick={handleRetry}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button 
                onClick={() => navigate('/login')}
                variant="outline"
                className="border-slate-600 text-white hover:bg-slate-700"
              >
                Go to Login
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-6">
              Retry attempt: {retryCount}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main Content
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <div className="bg-slate-800/80 backdrop-blur-sm border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                size="sm"
                className="text-gray-300 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">
                My Content Analyses
              </h1>
            </div>
            <Button
              onClick={handleRetry}
              variant="outline"
              size="sm"
              className="border-slate-600 text-white hover:bg-slate-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Filters */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
            {[
              { value: 'all', label: 'All', icon: Filter },
              { value: 'text', label: 'Text', icon: Type },
              { value: 'image', label: 'Images', icon: Image },
              { value: 'video', label: 'Videos', icon: Video },
              { value: 'link', label: 'Links', icon: LinkIcon }
            ].map(({ value, label, icon: Icon }) => (
              <Button
                key={value}
                onClick={() => setFilterType(value)}
                size="sm"
                variant={filterType === value ? 'default' : 'outline'}
                className={filterType === value 
                  ? 'bg-indigo-600 hover:bg-indigo-700' 
                  : 'border-slate-600 text-white hover:bg-slate-700'
                }
              >
                <Icon className="w-4 h-4 mr-2" />
                {label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {filteredAnalyses.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-12 text-center">
              <Sparkles className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                {filterType === 'all' 
                  ? 'No Analyses Yet' 
                  : `No ${filterType} analyses found`
                }
              </h3>
              <p className="text-gray-400 mb-6">
                Start analyzing content to see your history here
              </p>
              <Button 
                onClick={() => navigate('/')}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                Start Analyzing
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredAnalyses.map((analysis) => (
              <Card 
                key={analysis.id} 
                className="bg-slate-800/50 border-slate-700 hover:border-indigo-500/50 transition-all duration-200"
              >
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                    <div className="flex flex-wrap gap-2">
                      <Badge className="bg-indigo-600 flex items-center gap-1">
                        {getContentIcon(analysis.content_type)}
                        {analysis.content_type}
                      </Badge>
                      <Badge className="bg-purple-600">
                        {analysis.dominant_emotion}
                      </Badge>
                      {analysis.enhancement && (
                        <Badge className="bg-green-600">Enhanced</Badge>
                      )}
                    </div>
                    <span className="text-sm text-gray-400">
                      {new Date(analysis.timestamp).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>

                  {analysis.content && (
                    <p className="text-gray-300 mb-4 line-clamp-2 text-sm">
                      {analysis.content.substring(0, 200)}
                      {analysis.content.length > 200 && '...'}
                    </p>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => navigate(`/analysis/${analysis.id}`)}
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View
                    </Button>
                    
                    <Button
                      onClick={() => handleDownloadPDF(analysis)}
                      size="sm"
                      variant="outline"
                      className="border-slate-600 text-white hover:bg-slate-700"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                    
                    <Button
                      onClick={() => handleDownloadWord(analysis)}
                      size="sm"
                      variant="outline"
                      className="border-slate-600 text-white hover:bg-slate-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Word
                    </Button>
                    
                    <ShareButton
                      analysisId={analysis.id}
                      size="sm"
                      variant="outline"
                      className="border-slate-600 text-white hover:bg-slate-700"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Share
                    </ShareButton>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyContentAnalyses;
